// Stacked columns (one column per day, one segment per series): ≤ 24px columns, 2px surface gap
// between segments, rounded data-end on the top segment, per-column hover + arrow-key focus.

import { useMemo, useState, type KeyboardEvent } from "react";
import { ChartTooltip, useWidth } from "./ChartCard";
import { CHROME } from "./palette";
import { axisLabel, linear, niceTicks, roundedTopBar, sparseLabelIndices } from "./scale";

export interface StackSeries {
  key: string;
  label: string;
  color: string;
}

export interface StackColumn {
  key: string;
  label: string;
  /** Long label for the tooltip. */
  title: string;
  values: Record<string, number>;
}

const HEIGHT = 230;
const PAD = { top: 12, right: 8, bottom: 28, left: 40 };
const GAP = 2;

export function StackedBars({ series, columns, unit = "actions" }: { series: StackSeries[]; columns: StackColumn[]; unit?: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);

  const geo = useMemo(() => {
    if (width <= 0 || columns.length === 0) return null;
    const totals = columns.map((c) => series.reduce((s, se) => s + (c.values[se.key] ?? 0), 0));
    const ticks = niceTicks(0, Math.max(1, ...totals), { target: 4, zero: true, integer: true });
    const plotW = Math.max(10, width - PAD.left - PAD.right);
    const plotH = HEIGHT - PAD.top - PAD.bottom;
    const band = plotW / columns.length;
    const barW = Math.max(2, Math.min(24, band * 0.62));
    const y = linear([0, ticks[ticks.length - 1]!], [PAD.top + plotH, PAD.top]);
    const base = PAD.top + plotH;
    const bars = columns.map((c, i) => {
      const x = PAD.left + band * i + (band - barW) / 2;
      let cursor = base;
      const segs: { key: string; color: string; d: string }[] = [];
      const present = series.filter((se) => (c.values[se.key] ?? 0) > 0);
      present.forEach((se, j) => {
        const v = c.values[se.key] ?? 0;
        const h = base - y(v);
        const top = cursor - h;
        const isTop = j === present.length - 1;
        // 2px surface gap above every segment but the top one.
        const drawH = isTop ? h : Math.max(0.5, h - GAP);
        const drawY = isTop ? top : top + GAP;
        segs.push({ key: se.key, color: se.color, d: isTop ? roundedTopBar(x, drawY, barW, drawH, 4) : `M${x},${drawY}h${barW}v${drawH}h${-barW}Z` });
        cursor = top;
      });
      return { x, segs, total: totals[i]!, center: x + barW / 2 };
    });
    return { ticks, y, base, plotW, plotH, band, bars, labels: sparseLabelIndices(columns.length, plotW, 64) };
  }, [width, columns, series]);

  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    setActive((cur) => {
      const n = columns.length;
      if (e.key === "Home") return 0;
      if (e.key === "End") return n - 1;
      const start = cur ?? n - 1;
      return Math.min(n - 1, Math.max(0, start + (e.key === "ArrowLeft" ? -1 : 1)));
    });
  };

  const col = active !== null ? columns[active] : null;
  const total = geo && active !== null ? geo.bars[active]!.total : 0;

  return (
    <div ref={ref} className="an-plot" style={{ height: HEIGHT }}>
      {geo ? (
        <svg
          width={width}
          height={HEIGHT}
          className="an-svg"
          role="img"
          aria-label={`Stacked columns of ${unit} per day for ${series.map((s) => s.label).join(", ")}. Use arrow keys to read each day.`}
          tabIndex={0}
          onKeyDown={onKey}
          onBlur={() => setActive(null)}
        >
          {geo.ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + geo.plotW} y1={geo.y(t)} y2={geo.y(t)} stroke={t === 0 ? CHROME.axis : CHROME.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={geo.y(t)} dy="0.32em" textAnchor="end" className="an-axis">
                {axisLabel(t)}
              </text>
            </g>
          ))}
          {geo.labels.map((i) => (
            <text key={i} x={geo.bars[i]!.center} y={HEIGHT - 8} textAnchor={i === 0 ? "start" : i === columns.length - 1 ? "end" : "middle"} className="an-axis">
              {columns[i]!.label}
            </text>
          ))}
          {geo.bars.map((b, i) => (
            <g key={columns[i]!.key} opacity={active === null || active === i ? 1 : 0.55}>
              {b.segs.map((s) => (
                <path key={s.key} d={s.d} fill={s.color} />
              ))}
            </g>
          ))}
          {/* hit targets: the whole band, not just the painted bar */}
          {geo.bars.map((_, i) => (
            <rect
              key={`hit-${i}`}
              x={PAD.left + geo.band * i}
              y={PAD.top}
              width={geo.band}
              height={geo.plotH}
              fill="transparent"
              onPointerEnter={() => setActive(i)}
              onPointerLeave={() => setActive(null)}
            />
          ))}
        </svg>
      ) : null}
      {geo && col ? (
        <ChartTooltip
          x={geo.bars[active!]!.center}
          y={PAD.top}
          width={width}
          title={`${col.title} · ${total.toLocaleString("en-US")} ${unit}`}
          rows={series
            .slice()
            .reverse()
            .filter((s) => (col.values[s.key] ?? 0) > 0)
            .map((s) => ({ color: s.color, label: s.label, value: (col.values[s.key] ?? 0).toLocaleString("en-US") }))}
        />
      ) : null}
    </div>
  );
}
